import { Platform } from 'react-native'
import * as SecureStore from 'expo-secure-store'
import Constants from 'expo-constants'

const PREFIX = Constants.expoConfig?.extra?.appAudience === 'admin' ? 'warcarp_admin_' : 'warcarp_'

/**
 * SecureStore options: AFTER_FIRST_UNLOCK means items written here are
 * accessible after the device has been unlocked once after boot (the normal
 * user journey). Without this the default accessibility level may be
 * WHEN_UNLOCKED_THIS_DEVICE_ONLY, which can cause "User interaction is not
 * allowed" errors when the keychain is queried while the device is still
 * locked (e.g. a background push arriving before first unlock — WARCARP-APP-F).
 */
const SECURE_STORE_OPTIONS: SecureStore.SecureStoreOptions = {
  keychainAccessible: SecureStore.AFTER_FIRST_UNLOCK,
}

export async function setItem(key: string, value: string): Promise<void> {
  if (Platform.OS === 'web') {
    localStorage.setItem(PREFIX + key, value)
  } else {
    await SecureStore.setItemAsync(PREFIX + key, value, SECURE_STORE_OPTIONS)
  }
}

/**
 * Read a value from SecureStore. Returns null on any read failure (including
 * the "User interaction is not allowed" error that fires when the iOS Keychain
 * is accessed while the device is locked — WARCARP-APP-F). The failure is
 * logged as a breadcrumb so the timeline is available when something else fires.
 */
export async function getItem(key: string): Promise<string | null> {
  if (Platform.OS === 'web') {
    return localStorage.getItem(PREFIX + key)
  }
  try {
    return await SecureStore.getItemAsync(PREFIX + key, SECURE_STORE_OPTIONS)
  } catch (err) {
    // Silently returning null keeps the app from crashing on an unhandled
    // rejection. The breadcrumb preserves observability without creating a
    // spurious Sentry issue — the caller (e.g. apiFetch) already handles a
    // null access token gracefully (no auth header → 401 → refresh flow).
    //
    // We deliberately avoid importing from reporting.ts here to prevent a
    // circular import; we use a try/catch dynamic import fallback instead,
    // or just rely on the breadcrumb to be flushed with the next Sentry event.
    // For simplicity we console.warn in dev and rely on the Sentry global
    // breadcrumb buffer for production diagnostics.
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.warn('[storage] SecureStore read failed:', err)
    }
    try {
      // Dynamic Sentry import to avoid pulling Sentry into the module graph
      // at startup and to sidestep the circular reporting.ts dependency.
      const { default: Sentry } = await import('@sentry/react-native')
      Sentry.addBreadcrumb({
        category: 'storage',
        message: `SecureStore read failed for key "${PREFIX + key}"`,
        level: 'warning',
        data: { error: err instanceof Error ? err.message : String(err) },
      })
    } catch {
      // Sentry not available (tests, local dev with no DSN) — swallow.
    }
    return null
  }
}

export async function removeItem(key: string): Promise<void> {
  if (Platform.OS === 'web') {
    localStorage.removeItem(PREFIX + key)
  } else {
    await SecureStore.deleteItemAsync(PREFIX + key, SECURE_STORE_OPTIONS)
  }
}
